/**
 * Throw system data — types and tuning constants.
 * Pure module; no Phaser, no Combat imports (avoids circular deps).
 */

/** Direction the victim travels when thrown. */
export type ThrowType = 'forward' | 'back' | 'overhead';

/**
 * Team identifier for throw ownership.
 * Mirrors Combat.Team ('player' | 'enemy') but declared here so throws.ts
 * stays free of Combat imports and the dep graph stays acyclic.
 */
export type ThrowerTeam = 'player' | 'enemy';

/** Mutable state attached to an entity while it is in flight. */
export interface ThrownState {
  vx: number; // horizontal velocity (px/frame)
  vy: number; // elevation velocity: upward = positive (px/frame)
  elevation: number; // height above ground plane (px)
  landingDamage: number;
  flyDamage: number; // damage dealt to bystanders on collision during flight
  throwerTeam: ThrowerTeam;
  /** Combat IDs already hit mid-flight — each bystander takes damage at most once. */
  readonly alreadyHit: Set<number>;
}

/** Per-move throw parameters. */
export interface ThrowSpec {
  /** Damage applied to the thrown target on landing. */
  landingDamage: number;
  /** Damage dealt to bystanders hit during flight. */
  flyDamage: number;
  /** Max horizontal distance to initiate a grab (px). */
  grabRange: number;
}

/** Standard throw — requires target to be in hitstun. */
export const DEFAULT_THROW_SPEC: ThrowSpec = {
  landingDamage: 14,
  flyDamage: 10,
  grabRange: 80,
};

/** Command throw — heavy heroes only; does not require hitstun. */
export const COMMAND_THROW_SPEC: ThrowSpec = {
  landingDamage: 22,
  flyDamage: 14,
  grabRange: 90,
};

/** Frames of hitstun applied to the thrown entity on landing. */
export const LANDING_HITSTUN = 30;

// ---------------------------------------------------------------------------
// Grab-clinch constants (MY-1796)
// ---------------------------------------------------------------------------

/** Damage per pummel hit (knee / headbutt) during a grab clinch. */
export const PUMMEL_DAMAGE = 4;

/** Frames the grabber must wait before landing another pummel. */
export const PUMMEL_COOLDOWN = 10;

/** Radius (px) of the ground-slam AoE when a suplexed victim hits the floor. */
export const SUPLEX_AOE_RADIUS = 60;

/**
 * Suplex throw spec — UP+HEAVY during clinch.
 * High landing damage; no mid-flight bystander damage (victim crashes straight
 * down rather than arcing across the screen).
 */
export const SUPLEX_SPEC: ThrowSpec = {
  landingDamage: 28,
  flyDamage: 0,
  grabRange: 80,
};
